import { type Voice } from '@/domain/book';
import {
  type ITTSEngine,
  type SpeakChunk,
  type SpeakOptions,
  type TTSHandlers,
} from '@/services/TTSService';

/** One synthesized clip: mono float samples plus the rate they were sampled at (Kokoro emits 24kHz). */
export interface PcmAudio {
  data: Float32Array;
  sampleRate: number;
}

/**
 * The neural synth, isolated behind an interface so the queueing logic can be unit-tested with a
 * fake. The production implementation ({@link KokoroWorkerSynth}) runs the model in a Web Worker.
 */
export interface IKokoroSynth {
  /** Load the model, reporting 0..1 progress. Resolves when ready to synthesize. */
  load(onProgress: (progress: number) => void): Promise<void>;
  /** The model's voices, available after {@link load}. */
  voices(): Voice[];
  /** Synthesize one sentence. `speed` is the reading tempo (Kokoro re-times, so no pitch shift). */
  synth(text: string, voiceId: string | undefined, speed: number): Promise<PcmAudio>;
  readonly ready: boolean;
}

/** Opaque playback handle returned by {@link IAudioSink.play}; only the sink interprets it. */
export type AudioHandle = unknown;

/** Web Audio, isolated so playback ordering can be tested without a real `AudioContext`. */
export interface IAudioSink {
  /** Play a clip to its end, calling `onEnded` when it finishes naturally. Returns a stop handle. */
  play(pcm: PcmAudio, onEnded: () => void): AudioHandle;
  /** Stop a handle immediately. Its `onEnded` must NOT fire afterwards. */
  stop(handle: AudioHandle): void;
}

/** A Kokoro engine that also exposes an explicit, progress-reporting model load for the router. */
export interface ILoadableTTSEngine extends ITTSEngine {
  load(onProgress: (progress: number) => void): Promise<void>;
}

type ClipState = 'pending' | 'ready' | 'playing' | 'done' | 'error';

/** One sentence of a chunk. A chunk is split into these so the first sentence can start playing in
    ~2s instead of after the whole paragraph synthesizes. `chunkId`/`isLast` map the sentences back
    to the player's one-callback-per-chunk contract. */
interface SubClip {
  chunkId: string;
  isLast: boolean;
  text: string;
  voiceId: string | undefined;
  speed: number;
  pcm: PcmAudio | null;
  state: ClipState;
}

/** Split into sentences, keeping terminal punctuation. Falls back to the whole string when there is
    no sentence break, and drops whitespace-only fragments. */
export function splitSentences(text: string): string[] {
  const parts = text.match(/[^.!?]+(?:[.!?]+|$)/g) ?? [text];
  return parts.map((s) => s.trim()).filter((s) => s.length > 0);
}

/**
 * Drives Kokoro as an {@link ITTSEngine}: the same enqueue/stop/callbacks contract the browser
 * synth honours, so `PlayerStore` treats them identically.
 *
 * Why the extra machinery vs the Web Speech engine? Web Speech owns its own queue and clock. Kokoro
 * hands back raw PCM, and even above realtime a whole paragraph takes seconds to synthesize. So this
 * class splits each chunk into sentences and runs a two-hand pipeline: synthesize the next sentence
 * (one at a time, in the worker) while the current one plays, then play strictly in order. The first
 * sentence starts almost immediately; the rest buffer under it.
 *
 * `onStart` fires once when a chunk's first sentence begins; `onEnd` once when its last sentence
 * finishes — so the player's position tracking is unchanged. A `stop()` bumps an epoch that
 * invalidates any synth still in flight and any pending clip-ended callback, so a seek can never
 * resurrect stale audio.
 */
export class KokoroTTSService implements ILoadableTTSEngine {
  private clips: SubClip[] = [];
  private synthing = false;
  private playing: AudioHandle | null = null;
  // Chunks whose onStart has already fired, so later sentences of the same chunk don't re-announce.
  private announced = new Set<string>();
  // Bumped on stop(). Async results (synth promise, clip-ended) carry the epoch they started under
  // and are discarded if it has moved on.
  private epoch = 0;
  private handlers: TTSHandlers = {};

  constructor(
    private synth: IKokoroSynth,
    private sink: IAudioSink,
  ) {}

  async load(onProgress: (progress: number) => void): Promise<void> {
    if (this.synth.ready) return;
    await this.synth.load(onProgress);
  }

  initialize(): Promise<void> {
    return this.load(() => {});
  }

  voices(): Voice[] {
    return this.synth.voices();
  }

  setHandlers(handlers: TTSHandlers): void {
    this.handlers = handlers;
  }

  get ready(): boolean {
    return this.synth.ready;
  }

  enqueue(chunk: SpeakChunk, opts: SpeakOptions): void {
    if (!this.synth.ready) {
      this.handlers.onError?.(chunk.id, new Error('Kokoro model is not loaded'));
      return;
    }
    const sentences = splitSentences(chunk.text);
    if (sentences.length === 0) {
      // Nothing speakable (whitespace only): announce and finish so the player still advances.
      const epoch = this.epoch;
      queueMicrotask(() => {
        if (epoch !== this.epoch) return;
        this.handlers.onStart?.(chunk.id);
        this.handlers.onEnd?.(chunk.id);
      });
      return;
    }
    sentences.forEach((text, i) => {
      this.clips.push({
        chunkId: chunk.id,
        isLast: i === sentences.length - 1,
        text,
        voiceId: opts.voiceId,
        speed: opts.rate,
        pcm: null,
        state: 'pending',
      });
    });
    this.pumpSynth();
    this.pumpPlay();
  }

  stop(): void {
    this.epoch++;
    if (this.playing !== null) {
      this.sink.stop(this.playing);
      this.playing = null;
    }
    this.clips = [];
    this.announced.clear();
    this.synthing = false; // an in-flight synth still resolves, but the epoch check drops its result
  }

  /** Synthesize the earliest pending sentence. One at a time: the worker holds a single model. */
  private pumpSynth = (): void => {
    if (this.synthing) return;
    const clip = this.clips.find((c) => c.state === 'pending');
    if (!clip) return;
    this.synthing = true;
    const epoch = this.epoch;
    this.synth
      .synth(clip.text, clip.voiceId, clip.speed)
      .then((pcm) => {
        this.synthing = false;
        if (epoch !== this.epoch) return; // stopped mid-synth: drop it
        clip.pcm = pcm;
        clip.state = 'ready';
        this.pumpSynth();
        this.pumpPlay();
      })
      .catch((error: unknown) => {
        this.synthing = false;
        if (epoch !== this.epoch) return;
        clip.state = 'error';
        // The player treats one chunk erroring as fatal and will stop() us; no need to skip ahead.
        this.handlers.onError?.(clip.chunkId, error);
      });
  };

  /** Play the head sentence once its audio is ready. Strict FIFO: a later ready sentence never jumps
      a still-synthesizing earlier one. */
  private pumpPlay = (): void => {
    if (this.playing !== null) return;
    const head = this.clips.find((c) => c.state !== 'done' && c.state !== 'error');
    if (!head || head.state !== 'ready' || !head.pcm) return;
    head.state = 'playing';
    const epoch = this.epoch;
    this.playing = this.sink.play(head.pcm, () => this.onClipEnded(head, epoch));
    if (!this.announced.has(head.chunkId)) {
      this.announced.add(head.chunkId);
      this.handlers.onStart?.(head.chunkId); // first sentence of the chunk: announce chunk start
    }
  };

  private onClipEnded = (clip: SubClip, epoch: number): void => {
    if (epoch !== this.epoch) return; // a stop() already cancelled this generation
    this.playing = null;
    clip.state = 'done';
    // Drop finished sentences off the front so the array stays small across a whole book.
    while (this.clips.length > 0 && (this.clips[0].state === 'done' || this.clips[0].state === 'error')) {
      this.clips.shift();
    }
    if (clip.isLast) {
      this.announced.delete(clip.chunkId);
      this.handlers.onEnd?.(clip.chunkId); // last sentence: the player may enqueue the next chunk here
    }
    this.pumpPlay();
    this.pumpSynth();
  };
}
